import React, { useEffect, useState } from 'react';
import { Star, MapPin } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import PhoneRevealButton from '@/components/PhoneRevealButton';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { useAuth } from '@/providers/AuthProvider';
import { useNavigate } from 'react-router-dom';
import { ProfessionalInterface } from '@/services/professionals/types';
import { useGoogleTranslate } from '@/hooks/useTranslation';
import { useDirection } from '@/providers/DirectionProvider';
import { useProfessionDisplay } from '@/hooks/useProfessionDisplay';
import { initializeProfessionsCacheNew, getAllProfessions } from '@/services/professions/getProfessionsNew';

interface ProfessionalCardProps extends Partial<ProfessionalInterface> {
  id: string;
  name: string;
  profession: string;
  rating: number;
  reviewCount: number;
  location: string;
  image: string;
  specialties: string[];
  phoneNumber?: string;
  verified?: boolean;
  onPhoneReveal?: (professionalName: string) => boolean;
  // NEW: The profession that was searched for - only show this profession
  searchedProfessionId?: string;
}

const ProfessionalCard: React.FC<ProfessionalCardProps> = ({
  id,
  name,
  profession,
  rating,
  reviewCount,
  location,
  image,
  specialties,
  phoneNumber,
  verified = false,
  onPhoneReveal,
  sub_specializations,
  main_profession,
  profession_ids,
  specialization_ids,
  searchedProfessionId
}) => {
  const { t, i18n } = useTranslation();
  const { direction } = useDirection();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [showLoginDialog, setShowLoginDialog] = useState(false);
  const [imgError, setImgError] = useState(false);
  const [cacheReady, setCacheReady] = useState(() => getAllProfessions().length > 0);
  const isHebrew = i18n.language === 'he';

  // Initialize professions cache if not ready - setState triggers re-render
  useEffect(() => {
    if (!cacheReady) {
      initializeProfessionsCacheNew().then(() => setCacheReady(true));
    }
  }, [cacheReady]);

  // Get the correct profession and specializations to display
  const { professionLabel, specializationLabels, displayedProfessionId } = useProfessionDisplay({
    professionIds: profession_ids,
    specializationIds: specialization_ids,
    searchedProfessionId,
    profession,
    mainProfession: main_profession
  });

  // Use profession prop as fallback if cache/label not ready
  const displayProfession = professionLabel || profession;
  
  // Prepare texts for translation - always include profession (either from cache or prop)
  const textsToTranslate = [name, displayProfession, location, ...specializationLabels];

  // Translate texts
  const { translatedTexts } = useGoogleTranslate({
    texts: textsToTranslate,
    enabled: !isHebrew
  });

  // Extract translated values - use displayProfession as fallback
  const translatedName = translatedTexts[0] || name;
  const translatedProfession = translatedTexts[1] || displayProfession;
  const translatedLocation = translatedTexts[2] || location;
  const translatedSpecialties = translatedTexts.slice(3);

  // Build profile link with profession context
  const profileLink = searchedProfessionId 
    ? `/professional/${id}?profession=${searchedProfessionId}`
    : `/professional/${id}`;
  const handlePhoneClick = () => {
    if (!user) {
      setShowLoginDialog(true);
      return false;
    }
    if (onPhoneReveal) {
      return onPhoneReveal(name);
    }
    return true;
  };
  const handleLoginRedirect = () => {
    setShowLoginDialog(false);
    navigate('/login', {
      state: {
        returnUrl: window.location.pathname
      }
    });
  };
  const handleImageError = () => {
    setImgError(true);
  };

  // Use a better fallback image with appropriate size
  const imageUrl = imgError ? 'https://images.unsplash.com/photo-1487058792275-0ad4aaf24ca7?auto=format&fit=crop&q=80&w=800&h=450' : image;
  return <>
      <div className="glass-card group hover:-translate-y-1 hover:shadow-lg transition-all duration-300">
        <div className="relative overflow-hidden rounded-t-xl h-48">
          <img src={imageUrl} alt={translatedName} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" onError={handleImageError} loading="lazy" />
        </div>

        <div className="p-5">
          <div className="flex justify-between items-start">
            <div>
              <h3 className="text-lg font-semibold">{translatedName}</h3>
              <p className="text-gray-600 text-sm">{translatedProfession}</p>
            </div>
            <div className="flex items-center gap-1">
              <span className="text-lg font-bold text-[#0040a1]">{rating}<span className="text-xs text-gray-400 font-normal">/5</span></span>
              <Star className="h-4 w-4 fill-yellow-400 text-yellow-400" />
              <span className="text-xs text-gray-500">({reviewCount})</span>
            </div>
          </div>

          <div className="flex items-center mt-2 text-gray-500 text-sm">
            <MapPin size={14} className={direction === 'rtl' ? 'ml-1' : 'mr-1'} />
            <span>{translatedLocation}</span>
          </div>

          {/* Specialties tags - only show specializations belonging to displayed profession */}
          {translatedSpecialties.length > 0 && (
            <div className="mt-3">
              <div className="flex flex-wrap gap-1">
                {translatedSpecialties.map((specialty, index) => (
                  <span key={index} className="text-xs bg-blue-50 text-blue-700 px-2 py-1 rounded-full">
                    {specialty}
                  </span>
                ))}
                {specializationLabels.length > 3 && (
                  <span className="text-xs text-gray-500">+{specializationLabels.length - 3}</span>
                )}
              </div>
            </div>
          )}

          <div className="mt-4 space-y-2">
            <PhoneRevealButton professionalName={name} professionalId={id} profession={profession} onBeforeReveal={handlePhoneClick} />

            <Button asChild variant="outline" className="w-full border-teal-500 text-teal-600 hover:bg-teal-50">
              <Link to={profileLink}>{t('search.professionalCard.viewProfile')}</Link>
            </Button>
          </div>
        </div>
      </div>

      {/* Login Dialog */}
      <Dialog open={showLoginDialog} onOpenChange={setShowLoginDialog}>
        <DialogContent className="sm:max-w-md" dir={direction}>
          <DialogHeader>
            <DialogTitle>{t('dialogs.loginRequired.title')}</DialogTitle>
            <DialogDescription>
              {t('dialogs.loginRequired.description')}
            </DialogDescription>
          </DialogHeader>
          <div className="flex flex-col gap-4 py-4">
            <p className="text-sm text-muted-foreground">
              {t('dialogs.loginRequired.message')}
            </p>
          </div>
          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button variant="outline" onClick={() => setShowLoginDialog(false)} className="w-full sm:w-auto">
              {t('common.cancel')}
            </Button>
            <Button onClick={handleLoginRedirect} className="w-full sm:w-auto bg-[#00D09E] hover:bg-[#00C090]">
              {t('dialogs.loginRequired.goToLoginButton')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>;
};
export default ProfessionalCard;